import dynamic from 'next/dynamic';
import React, { useState, Suspense, useEffect } from 'react';
import LazyLoad from 'react-lazy-load';
import PropTypes from 'prop-types';
import { stripHtml } from '../../adapters/helpers/Utils';
import ImageFactory from '../../adapters/cloudinary/ImageFactory';
import Block from '../../adapters/helpers/Block';
import { ComponentFactoryConstants } from '../../adapters/helpers/Constants';
import { optimizeImage } from '../../adapters/helpers/pagespeedHelper';
import ArticleListCarousel from '../ArticleListCarousel/ArticleListCarousel';
const Heading = dynamic(() => import('../Heading/Heading'));
const Icon = dynamic(() => import('../Icon/Icon'));
import Link from 'next/link';
import Image from 'next/image';
import Loader from '../../adapters/helpers/Loader';
const MarkdownText = dynamic(() => import('../MarkdownText/MarkdownText'));
const IArticleListProps = {
  contentType: PropTypes.oneOf([ComponentFactoryConstants.ArticleListBlock]),
  classNames: PropTypes.string,
  className: PropTypes.string,
  name: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  anchorId: PropTypes.string,
  category: PropTypes.object,
  articles: PropTypes.array,
  articleList: PropTypes.array,
  ctaLabel: PropTypes.string,
  ctaLink: PropTypes.object,
  isCategoryLanding: PropTypes.bool,
  categories: PropTypes.arrayOf(PropTypes.object)
};
export default function ArticleListBlock(props) {
  var _fields$category, _fields$category$fiel;
  const {
    extraAttributes
  } = props;
  const block = new Block(props);
  const fields = block.getFields();
  const categoryName = (fields == null ? void 0 : (_fields$category = fields.category) == null ? void 0 : (_fields$category$fiel = _fields$category.fields) == null ? void 0 : _fields$category$fiel.name) || '';
  let articles = [];
  let isCategoryLanding = false;
  if (categoryName && (extraAttributes == null ? void 0 : extraAttributes.articles)) {
    articles = extraAttributes == null ? void 0 : extraAttributes.articles[categoryName];
  } else {
    articles = fields == null ? void 0 : fields.articleList;
    isCategoryLanding = true;
  }
  return articles && articles.length > 0 ? <ArticleList {...fields} anchorId={block.getAnchorId()} articles={articles} isCategoryLanding={isCategoryLanding} /> : null;
}
ArticleListBlock.propTypes = {
  extraAttributes: PropTypes.object,
  document: PropTypes.shape({
    fields: PropTypes.exact(IArticleListProps)
  })
};
export function ArticleList(props) {
  var _ctaLink$fields, _ctaLink$fields2;
  const {
    classNames,
    className = classNames
  } = props;
  const {
    title,
    anchorId,
    articles,
    isCategoryLanding,
    ctaLabel,
    ctaLink,
    description,
    leftArrowIcon,
    rightArrowIcon
  } = props;
  const [showMore, setShowMore] = useState(true);
  const variationB = articles.length <= 2;
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Use a global flag to ensure only one block scrolls per load
    if (!window.__articleListScrolled) window.__articleListScrolled = {};
    let rafId;
    let attempts = 0;
    const maxAttempts = 40;
    function scrollToHash() {
      const hash = window.location.hash.replace("#", "");
      if (hash === anchorId && !window.__articleListScrolled[hash]) {
        const target = document.getElementById(hash);
        if (target) {
          var _document$querySelect;
          const headerHeight = ((_document$querySelect = document.querySelector(".zone-header")) == null ? void 0 : _document$querySelect.offsetHeight) || 162;
          const top = target.getBoundingClientRect().top + window.pageYOffset - headerHeight;
          if (Math.abs(window.pageYOffset - top) > 5) {
            window.scrollTo({
              top,
              behavior: "smooth"
            });
          }
          if (attempts < maxAttempts && Math.abs(window.pageYOffset - top) > 5) {
            attempts++;
            rafId = requestAnimationFrame(scrollToHash);
          } else {
            // Final correction after a short delay
            setTimeout(() => {
              const finalTop = target.getBoundingClientRect().top + window.pageYOffset - headerHeight;
              window.scrollTo({
                top: finalTop,
                behavior: "auto"
              });
              window.__articleListScrolled[hash] = true;
            }, 600);
          }
        }
      }
    }
    rafId = requestAnimationFrame(scrollToHash);
    window.addEventListener("hashchange", () => {
      // Reset the flag on hash change so new scroll can happen
      window.__articleListScrolled = {};
      scrollToHash();
    });
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("hashchange", scrollToHash);
    };
  }, [anchorId]);
  return <Suspense fallback={<Loader />}>
     <div className={`alp ob-article-list ob-additional-reading ${isCategoryLanding ? 'ob-article-list--landing' : ''} ${variationB ? 'ob-additional-reading--wide' : ''} ${className || ''}`} id={`${anchorId}`}>
        <Suspense fallback={<Loader />}>
            <Heading className="ob-additional-reading__heading" tag={isCategoryLanding ? 'h3' : 'h2'}>
                {title}
            </Heading>
         </Suspense>
        <MarkdownText className={'article-list-description'} value={description} />
         <LazyLoad offset={100}>
            <div className="ob-additional-reading__articles">
                <ul className="ob-additional-reading__list">
                    {articles.filter((res, index) => showMore ? index < 6 : index < articles.length).map((article, i) => {
              var _article$fields, _overview$fields, _thumbnail$fields, _thumbnail$fields$ass, _thumbnail$fields$ass2, _thumbnail$fields$ass3, _article$fields2, _article$fields2$slug, _article$fields2$slug2, _overview$fields2, _overview$fields3, _overview$fields4, _overview$fields5, _overview$fields6, _overview$fields6$slu, _overview$fields6$slu2, _overview$fields7, _overview$fields8, _overview$fields9, _overview$fields10, _overview$fields11;
              const overview = article == null ? void 0 : (_article$fields = article.fields) == null ? void 0 : _article$fields.articleOverview;
              const thumbnail = overview == null ? void 0 : (_overview$fields = overview.fields) == null ? void 0 : _overview$fields.thumbnailImage;
              const contentThumbnail = thumbnail == null ? void 0 : (_thumbnail$fields = thumbnail.fields) == null ? void 0 : (_thumbnail$fields$ass = _thumbnail$fields.asset) == null ? void 0 : (_thumbnail$fields$ass2 = _thumbnail$fields$ass.fields) == null ? void 0 : (_thumbnail$fields$ass3 = _thumbnail$fields$ass2.file) == null ? void 0 : _thumbnail$fields$ass3.url;
              const pageSlug = article == null ? void 0 : (_article$fields2 = article.fields) == null ? void 0 : (_article$fields2$slug = _article$fields2.slug) == null ? void 0 : (_article$fields2$slug2 = _article$fields2$slug.fields) == null ? void 0 : _article$fields2$slug2.slug;
              return <li className="ob-additional-reading__item" key={i}>
                                <Link className="ob-additional-reading__item-link event_internal_link" data-action-detail={(overview == null ? void 0 : (_overview$fields2 = overview.fields) == null ? void 0 : _overview$fields2.linkDataActionDetail) ? stripHtml(overview == null ? void 0 : overview.fields.linkDataActionDetail) : stripHtml(overview == null ? void 0 : (_overview$fields3 = overview.fields) == null ? void 0 : _overview$fields3.title)} href={pageSlug ? pageSlug : (overview == null ? void 0 : (_overview$fields4 = overview.fields) == null ? void 0 : _overview$fields4.aricleOverviewLink) ? overview == null ? void 0 : (_overview$fields5 = overview.fields) == null ? void 0 : _overview$fields5.aricleOverviewLink : overview == null ? void 0 : (_overview$fields6 = overview.fields) == null ? void 0 : (_overview$fields6$slu = _overview$fields6.slug) == null ? void 0 : (_overview$fields6$slu2 = _overview$fields6$slu.fields) == null ? void 0 : _overview$fields6$slu2.slug}>
                                    <div className="ob-additional-reading__item-image">
                                        {!!contentThumbnail && <LazyLoad offset={100} placeholder={<picture className="ob-additional-reading__item-pic">
                                                    <Image className="ob-additional-reading__item-img" alt={`${overview == null ? void 0 : (_overview$fields7 = overview.fields) == null ? void 0 : _overview$fields7.title}`} height={150} width={window.innerWidth > 767 ? 275 : 124} priority={true} />
                                                </picture>}>
                                                <Suspense fallback={<Loader />}>
                                                <picture className="ob-additional-reading__item-pic">
                                                    {variationB && <source media={ComponentFactoryConstants.mediaUpper} srcSet={optimizeImage(contentThumbnail)} />}

                                                    <source media={ComponentFactoryConstants.mediaLower} srcSet={optimizeImage(contentThumbnail)} />
                                                    <source srcSet={optimizeImage(contentThumbnail)} />

                                                {contentThumbnail && <Image className="ob-additional-reading__item-img" alt={`${overview == null ? void 0 : (_overview$fields8 = overview.fields) == null ? void 0 : _overview$fields8.title} `} src={optimizeImage(contentThumbnail)} srcSet={optimizeImage(contentThumbnail)} height={150} width={window.innerWidth > 767 ? 275 : 124} priority={true} />}
                                                </picture>
                                                </Suspense>
                                                </LazyLoad>}
                                        </div>
                                        
                                        <div className="ob-additional-reading__item-info">
                                            <div className="ob-additional-reading__item-title">
                                                {overview == null ? void 0 : (_overview$fields9 = overview.fields) == null ? void 0 : _overview$fields9.title}
                                            </div>
                                            <div className="ob-additional-reading__item-description">
                                                <p>{overview == null ? void 0 : (_overview$fields10 = overview.fields) == null ? void 0 : _overview$fields10.description}</p>
                                            </div>
                                            {!!(overview == null ? void 0 : (_overview$fields11 = overview.fields) == null ? void 0 : _overview$fields11.readTime) && <div className="ob-additional-reading__item-time">
                                                    {overview.fields.readTime} min read
                                                    </div>}
                                        </div>
                                    </Link>
                                </li>;
            })}
                </ul>
            </div>
            </LazyLoad>
            {isCategoryLanding && (ctaLink == null ? void 0 : (_ctaLink$fields = ctaLink.fields) == null ? void 0 : _ctaLink$fields.slug) ? <Suspense fallback={<Loader />}>
                        <Link href={ctaLink == null ? void 0 : (_ctaLink$fields2 = ctaLink.fields) == null ? void 0 : _ctaLink$fields2.slug} className={'ob-article-list__see-more-button'}>
                            <Icon className="ob-see-more-button__icon" roundedIcon={ComponentFactoryConstants.blue} name={ComponentFactoryConstants.chevronRight} />
                            {ctaLabel}
                        </Link>
                    </Suspense> : showMore && articles.length > 6 && <Suspense fallback={<Loader />}>
                        <div className={'ob-line'}>
                            <button onClick={() => {
            setShowMore(!showMore);
          }} data-action-detail={ctaLabel} className={'ob-article-list__see-more-button event_button_click'}>
                                {ctaLabel}
                                <img src={optimizeImage('/static/images/downicon.png')} alt='Downarrow' fetchPriority="high" />
                            </button>
                        </div>
                    </Suspense>}
            <ArticleListCarousel articles={articles} leftArrowIcon={leftArrowIcon} rightArrowIcon={rightArrowIcon}></ArticleListCarousel>
                   
            
     </div>
    </Suspense>;
}
ArticleList.propTypes = IArticleListProps;